import { useState, useMemo } from 'react'
import type { Component, NavigationLink, LocaleCode } from '../types/component'
import {
  NavigationMenu,
  NavigationMenuContent,
  NavigationMenuItem,
  NavigationMenuLink,
  NavigationMenuList,
  NavigationMenuTrigger,
  NavigationMenuViewport,
  navigationMenuTriggerStyle,
} from './ui/navigation-menu'
import { CategoryMegaMenu } from './CategoryMegaMenu'
import { Cart } from './Cart'
import { Wishlist } from './Wishlist'
import { getLocalizedText } from '../utils/localization'
import { cn } from '../utils/cn'

interface NavigationProps {
  component: Component
  style?: React.CSSProperties
  locale?: LocaleCode
  storeId: string
  apiUrl: string
  cartId?: string | null
  wishlistId?: string | null
  onCartUpdate?: (cartId: string) => void
  onWishlistUpdate?: (wishlistId: string) => void
}

export function Navigation({
  component,
  style,
  locale = 'en',
  storeId,
  apiUrl,
  cartId,
  wishlistId,
  onCartUpdate,
  onWishlistUpdate
}: NavigationProps) {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false)
  const [isMobileView, setIsMobileView] = useState(false)
  const config = component.config as any || {}

  const logoText = config.logoText as string
  const logoImage = config.logoImage as string
  const links = (config.links as NavigationLink[]) || []
  const rightLinks = (config.rightLinks as NavigationLink[]) || []
  const showCartButton = config.showCartButton !== false
  const showWishlistButton = config.showWishlistButton !== false
  const cartDisplayStyle = (config.cartDisplayStyle as string) || 'icon'
  const cartButtonText = (config.cartButtonText as string) || ''
  const cartShowItemCount = (config.cartShowItemCount ?? true) !== false
  const wishlistDisplayStyle = (config.wishlistDisplayStyle as string) || 'icon'
  const wishlistButtonText = (config.wishlistButtonText as string) || ''
  const wishlistShowItemCount = (config.wishlistShowItemCount ?? true) !== false
  const position = (config.position as string) || 'static'

  const navStyle: React.CSSProperties = {
    ...style,
    position: (position as any) || 'relative',
    top: position === 'sticky' ? 0 : undefined,
    zIndex: 9,
  }

  // Memoize the navigation menu to prevent unnecessary re-renders
  const navigationMenuContent = useMemo(() => {
    if (!links.length) {
      return <span className="text-sm text-muted-foreground">No links added</span>
    }

    return (
      <NavigationMenu>
        <NavigationMenuList>
          {links.map((link, index) => {
            const hasChildren = link.children && link.children.length > 0
            const isMegaMenu = link.isMegaMenu

            // Simple link
            if (!hasChildren && !isMegaMenu) {
              return (
                <NavigationMenuItem key={index}>
                  <NavigationMenuLink
                    href={link.href || '#'}
                    className={navigationMenuTriggerStyle()}
                  >
                    {getLocalizedText(link.label, locale)}
                  </NavigationMenuLink>
                </NavigationMenuItem>
              )
            }

            // Dropdown or mega menu
            return (
              <NavigationMenuItem key={index}>
                <NavigationMenuTrigger>{getLocalizedText(link.label, locale)}</NavigationMenuTrigger>
                <NavigationMenuContent>
                  {isMegaMenu ? (
                    <CategoryMegaMenu storeId={storeId} apiUrl={apiUrl} />
                  ) : (
                    <ul className="grid w-[200px] gap-3 p-4">
                      {link.children?.map((child, childIndex) => (
                        <li key={childIndex}>
                          <NavigationMenuLink asChild>
                            <a
                              href={child.href || '#'}
                              className="block select-none space-y-1 rounded-md p-3 leading-none no-underline outline-none transition-colors hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground"
                            >
                              <div className="text-sm font-medium leading-none">
                                {getLocalizedText(child.label, locale)}
                              </div>
                            </a>
                          </NavigationMenuLink>
                        </li>
                      ))}
                    </ul>
                  )}
                </NavigationMenuContent>
              </NavigationMenuItem>
            )
          })}
        </NavigationMenuList>
        <NavigationMenuViewport />
      </NavigationMenu>
    )
  }, [links, storeId, apiUrl, locale])

  return (
    <nav style={navStyle} className="border-b bg-background">
      <div className="px-6 py-4">
        <div className="flex items-center justify-between">
          {/* Logo */}
          <div className="flex items-center gap-2">
            {logoImage && <img src={logoImage} alt="Logo" className="h-8 w-auto" />}
            {logoText && <span className="text-xl font-bold">{logoText}</span>}
            {!logoImage && !logoText && (
              <span className="text-xl font-bold text-muted-foreground">Logo</span>
            )}
          </div>

          {/* Desktop Navigation */}
          {!isMobileView && (
            <div className="flex flex-1 justify-center">
              {navigationMenuContent}
            </div>
          )}

          {/* Right Side Menu */}
          {!isMobileView && (
            <div className="flex items-center gap-4">
              {rightLinks.length > 0 && (
                <div className="flex items-center gap-2">
                  {rightLinks.map((link, index) => (
                    <a
                      key={index}
                      href={link.href || '#'}
                      className="text-sm px-3 py-2 hover:bg-accent rounded transition-colors"
                    >
                      {getLocalizedText(link.label, locale)}
                    </a>
                  ))}
                </div>
              )}

              {showWishlistButton && (
                <Wishlist
                  component={{
                    ...component,
                    config: { displayStyle: wishlistDisplayStyle, showItemCount: wishlistShowItemCount, buttonText: wishlistButtonText }
                  }}
                  style={{}}
                  locale={locale}
                  storeId={storeId}
                  apiUrl={apiUrl}
                  wishlistId={wishlistId}
                  onWishlistUpdate={onWishlistUpdate}
                />
              )}

              {showCartButton && (
                <Cart
                  component={{
                    ...component,
                    config: { displayStyle: cartDisplayStyle, showItemCount: cartShowItemCount, buttonText: cartButtonText }
                  }}
                  style={{}}
                  locale={locale}
                  storeId={storeId}
                  apiUrl={apiUrl}
                  cartId={cartId}
                  onCartUpdate={onCartUpdate}
                />
              )}
            </div>
          )}

          {/* Mobile Menu Button */}
          {isMobileView && (
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="flex p-2 hover:bg-accent rounded transition-colors"
              aria-label="Toggle menu"
            >
            <div className="w-6 h-5 flex flex-col justify-between">
              <span
                className={cn(
                  "block h-0.5 w-full bg-current transition-transform",
                  isMobileMenuOpen && "rotate-45 translate-y-2"
                )}
              />
              <span
                className={cn(
                  "block h-0.5 w-full bg-current transition-opacity",
                  isMobileMenuOpen && "opacity-0"
                )}
              />
              <span
                className={cn(
                  "block h-0.5 w-full bg-current transition-transform",
                  isMobileMenuOpen && "-rotate-45 -translate-y-2"
                )}
              />
            </div>
            </button>
          )}
        </div>
      </div>

      {/* Mobile Sidebar */}
      {isMobileView && isMobileMenuOpen && (
        <>
          {/* Backdrop */}
          <div
            className="fixed inset-0 bg-black/50"
            onClick={() => setIsMobileMenuOpen(false)}
          />
          {/* Sidebar */}
          <div className="fixed top-0 right-0 h-full w-64 bg-background border-l shadow-xl z-[10001] overflow-y-auto">
            <div className="flex items-center justify-between p-4 border-b">
              <span className="font-semibold">Menu</span>
              <button
                onClick={() => setIsMobileMenuOpen(false)}
                className="p-2 hover:bg-accent rounded transition-colors"
                aria-label="Close menu"
              >
                <svg
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <line x1="18" y1="6" x2="6" y2="18" />
                  <line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              </button>
            </div>
            <div className="flex flex-col gap-2 p-4">
              {links.length > 0 ? (
                links.map((link, index) => (
                  <MobileNavigationItem
                    key={index}
                    link={link}
                    locale={locale}
                    onNavigate={() => setIsMobileMenuOpen(false)}
                  />
                ))
              ) : (
                <span className="text-sm text-muted-foreground">No links added</span>
              )}
            </div>
          </div>
        </>
      )}
    </nav>
  )
}

function MobileNavigationItem({ link, onNavigate, locale }: { link: NavigationLink; onNavigate: () => void; locale: LocaleCode }) {
  const [isOpen, setIsOpen] = useState(false)
  const hasChildren = link.children && link.children.length > 0

  if (!hasChildren) {
    return (
      <a
        href={link.href || '#'}
        onClick={onNavigate}
        className="text-sm font-medium hover:text-primary transition-colors py-2 px-3 rounded hover:bg-accent block"
      >
        {getLocalizedText(link.label, locale)}
      </a>
    )
  }

  return (
    <div>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="text-sm font-medium hover:text-primary transition-colors flex items-center justify-between w-full py-2 px-3 rounded hover:bg-accent"
      >
        {getLocalizedText(link.label, locale)}
        <span className={cn("text-xs transition-transform", isOpen && "rotate-180")}>
          ▼
        </span>
      </button>

      {isOpen && link.children && (
        <div className="mt-1 ml-2 flex flex-col gap-1">
          {link.children.map((child, index) => (
            <a
              key={index}
              href={child.href || '#'}
              onClick={onNavigate}
              className="text-sm text-muted-foreground hover:text-primary transition-colors py-2 px-3 rounded hover:bg-accent block"
            >
              {getLocalizedText(child.label, locale)}
            </a>
          ))}
        </div>
      )}
    </div>
  )
}
